import { type MarketItem } from '@/entities/market/model/market.types';
import { type Task, type Tasks } from '@/entities/task/model/task.types';
import {
  type UserLeaderboardShort,
  type UserType,
} from '@/entities/user/model/user.types';
import { type WHEEL_PRIZES } from '@/entities/wheel/model/wheel.types';

import { type Game } from '@/shared/store/games-store/types';
import { type PageableOptions, type PageableRes } from '@/shared/types/common';

enum LeagueColor {
  lightBlue = 'lightBlue',
  yellow = 'yellow',
  pink = 'pink',
  purple = 'purple',
  darkPurple = 'darkPurple',
  blue = 'blue',
  green = 'green',
  black = 'black',
  orange = 'orange',
  mint = 'mint',
}

export enum LeaderboardCategory {
  PARTNER = 'PARTNER',
  PLAYDECK = 'PLAYDECK',
}

export type LeaderboardsByCategory = Record<LeaderboardCategory, Leaderboard[]>;

export interface UserProfileLite {
  telegramId: number;
  avatar: string;
  username: string;
  firstName: string;
  lastName: string;
  level?: number;
}

interface UserProfileFull extends UserProfileLite {
  balance: number | null;
  relation: number | null;
}

export type UserLeaderboard = Omit<
  UserLeaderboardShort,
  'leaderboardType' | 'leagueNumber'
> & {
  user: UserProfileLite | UserType;
};

export enum LeaderboardContestType {
  XP = 'XP',
  REFERRAL = 'REFERRAL',
  PLAYCOIN = 'PLAYCOIN',
  PLAYDECK_GAMES = 'PLAYDECK_GAMES',
  GAME_LEVEL = 'GAME_LEVEL',
  DAILY_XP_IN_GAME = 'XP_IN_GAME',
}

export type PossibleLeaderboardPrize =
  | Exclude<
      WHEEL_PRIZES,
      | WHEEL_PRIZES.LOOTBOX
      | WHEEL_PRIZES.PARTNER
      | WHEEL_PRIZES.CLICKER_PLAYCOIN
      | WHEEL_PRIZES.CLICKER_XP
      | WHEEL_PRIZES.CLICKER_XP
    >
  | 'USDT';

export interface PrizePoolItem {
  amount: number | null;
  duration?: string | null;
  type: PossibleLeaderboardPrize;
}

export interface LeaderboardPrize {
  placeFrom: number;
  placeTo: number;
  prizes: PrizePoolItem[];
  score?: number;
}

export interface LeaderboardFriends {
  total: number;
  avatars: string[];
}

interface LeaderboardProgress {
  currentTotal: number;
  targetTotal: number;
}

export interface Leaderboard {
  id: number;
  category: LeaderboardCategory;
  contestType: LeaderboardContestType;

  title: string;
  description: string | null;

  image: string | null;
  prizePool: PrizePoolItem[];
  userPrizePool: PrizePoolItem[];

  timer: number | null | undefined;

  prizes: LeaderboardPrize[];
  friends: LeaderboardFriends;

  league?: number | null;
  nextLeagueLimit?: number | null;

  subtitle: string;
  color?: LeagueColor | null;
  isParticipant?: boolean;
  url?: string | null;
  partner?: string | null;
  joinButtonText?: string | null;
  place: number;
  score: number;
  lastPlace: number;
  progress?: LeaderboardProgress | null;
  prizesProcessed?: boolean | null;
  canClaimPrize?: boolean | null;

  gamePlayersToday?: number | null;
  game?: Game | null;

  totalScore?: number | null;
  totalParticipants?: number | null;

  qualified: boolean;
  top?: number | null;

  joinMarketItem?: MarketItem;
  qualificationScore?: number | null;
  qualificationTargetScore?: number | null;
  tasksGroup?: string | null;
  canClaimWelcomePrize?: boolean | null;
}

export type LeaderboardPlace = Pick<Leaderboard, 'place' | 'score' | 'id'> & {
  user: UserProfileFull;
  placeUdx: number;
  lastPlace: number;
  totalScore: number;
};

export interface WinnerByLBCategory {
  id: number;
  category: LeaderboardCategory;
  contestType: LeaderboardContestType;
  partner: string | null | undefined;
  places: LeaderboardPlace[];
}

export interface LeaderboardsState {
  topWinners: WinnerByLBCategory[] | undefined;
  leaderboards: Leaderboard[] | undefined;
  usersByLeaderboardId: Partial<
    Record<number, PageableRes<UserLeaderboard[]> | undefined>
  >;
  showFullReferralBanner: boolean | null;
  showFavGamesBanner: boolean | null;
  topLeadersPDFavGamesLB: UserLeaderboard[] | undefined;
  topLeadersDailyGameLB: UserLeaderboard[] | undefined;

  leaderboardMarketItemsDict: Record<number, MarketItem[]>;
  leaderboardTasks: Record<string, Task[]>;
}

export interface LeaderboardsStore extends LeaderboardsState {
  actions: {
    fetchLeaderboardsTop: () => Promise<WinnerByLBCategory[] | undefined>;

    fetchLeaderboards: () => Promise<Leaderboard[] | undefined>;
    fetchUsersByLeaderboard: (
      id: number,
      options?: PageableOptions
    ) => Promise<PageableRes<UserLeaderboard[]> | undefined>;
    fetchUsersOfLeaderboardByType: (
      type: LeaderboardContestType,
      options?: PageableOptions
    ) => Promise<PageableRes<UserLeaderboard[]> | undefined>;
    setReferralBannerVisibility: () => void;
    setFavGamesBannerVisibility: () => void;
    setLeadersPDFavGamesLB: (users: UserLeaderboard[]) => void;
    setLeadersDailyGameLB: (users: UserLeaderboard[]) => void;

    fetchLeaderboardMarketItems: (
      leaderboardId: number
    ) => Promise<MarketItem[] | undefined>;
    fetchLeaderboardTasks: (tasksGroup: string) => Promise<Tasks | undefined>;
    grabLeaderboardsAllPrizes: (leaderboardId: number) => Promise<void>;
  };
}

export enum TonBgLbStatus {
  OPENED = 'OPENED',
  CLOSED = 'CLOSED',
  ENDED = 'ENDED',
}
